import CheckCircle from "@mui/icons-material/CheckCircle";
import Send from "@mui/icons-material/Send";
import { Button, CircularProgress, Stack, Tooltip } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { SubmissionStatus } from "generated-graphql/graphql";
import { useUpdateSubmissionStatus } from "hooks/useSubmissionMutations";
import { useState } from "react";

interface UpdateSubmissionStatusButtonProps {
  submissionId?: string;
  currentStatus?: SubmissionStatus;
  isStaff: boolean;
  isEditMode: boolean;
  isSendToReviewersDisabled: boolean;
  loading: boolean;
  formLoading: boolean;
  onMoveToReview: () => void;
  onSuccess?: () => void;
  statusUpdatesDisabled?: boolean;
  markFilingCompleteDisabledTooltip?: string;
}

export function UpdateSubmissionStatusButton({
  submissionId,
  currentStatus,
  isStaff,
  isEditMode,
  isSendToReviewersDisabled,
  loading,
  formLoading,
  onMoveToReview,
  onSuccess,
  statusUpdatesDisabled,
  markFilingCompleteDisabledTooltip,
}: UpdateSubmissionStatusButtonProps) {
  const alerts = useAlerts();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [updateSubmissionStatus, { loading: updating }] = useUpdateSubmissionStatus();
  const markCompleteDisabled = updating || Boolean(statusUpdatesDisabled);
  const markCompleteDisabledTooltip = markFilingCompleteDisabledTooltip ?? "";
  const disableCompletionTooltipInactive = !statusUpdatesDisabled;

  const handleClick = () => {
    if (markCompleteDisabled) return;
    setConfirmDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!submissionId) return;
    try {
      await updateSubmissionStatus({
        variables: {
          input: {
            submissionId,
            submissionStatus: SubmissionStatus.FilingComplete,
          },
        },
      });

      alerts.success("Submission status updated to Filing Complete");
      setConfirmDialogOpen(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to update submission status:", error);
      alerts.error("Failed to update submission status");
    }
  };

  // Show "🎉 Filing Complete" text if status is FILING_COMPLETE
  if (currentStatus === SubmissionStatus.FilingComplete) {
    return (
      <Button
        variant="text"
        disabled
        sx={{
          color: "text.primary",
          fontSize: "1rem",
          fontWeight: 500,
          cursor: "default",
          "&.Mui-disabled": {
            color: "text.primary",
          },
        }}
      >
        🎉 Filing Complete
      </Button>
    );
  }

  // Show "Mark as Filing Complete" button if status is PROCESSING_SUBMISSION and user is staff
  if (isEditMode && currentStatus === SubmissionStatus.ProcessingSubmission && isStaff) {
    return (
      <>
        <Stack direction="row" spacing={1}>
          <Tooltip
            title={markCompleteDisabledTooltip}
            disableHoverListener={disableCompletionTooltipInactive}
            disableFocusListener={disableCompletionTooltipInactive}
            disableTouchListener={disableCompletionTooltipInactive}
          >
            <span>
              <Button
                variant="contained"
                color="success"
                startIcon={updating ? <CircularProgress size={20} /> : <CheckCircle />}
                onClick={handleClick}
                disabled={markCompleteDisabled}
                sx={{ textTransform: "uppercase" }}
              >
                Mark as filing complete
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <ConfirmDialog
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={handleConfirm}
          header="Confirm Status Update"
          info="Are you sure you want to mark this submission as Filing Complete? This action cannot be undone."
          confirmText="Mark as Complete"
          cancelText="Cancel"
        />
      </>
    );
  }

  // "Move to Review" only makes sense pre-customer-review; the regular Save button covers in-review edits.
  const canShowMoveToReview =
    !currentStatus ||
    currentStatus === SubmissionStatus.NotStarted ||
    currentStatus === SubmissionStatus.InProgress ||
    (isStaff && currentStatus === SubmissionStatus.EncampInitialReview);

  if (!canShowMoveToReview) {
    return null;
  }

  const buttonLabel = isStaff ? "Save & Move to Review" : "Move to Review";

  return (
    <Stack direction="row" spacing={1}>
      <Button
        variant="contained"
        disabled={isSendToReviewersDisabled || loading || formLoading}
        endIcon={<Send />}
        onClick={onMoveToReview}
        sx={{ textTransform: "uppercase" }}
      >
        {buttonLabel}
      </Button>
    </Stack>
  );
}
